import React from "react";
import Select from "react-select";
import { renderToString } from "react-dom/server";
import { Modal } from "react-bootstrap";

import WeeklyRealizationShift from "../tables/WeeklyRealizationShift";
import Loader from "@/modules/shared/ui/Loader";
import { IntlProviderWrapper, translate } from "@/modules/shared/IntlProviderWrapper";
import { dateFromWeekAndShift, weekNumber, yearNumber, getDateRangeStrForWeek, classNames } from "@/lib/util";
import { loadWeeklyRealization } from "../core/WeeklyRealization";
import { MaterialsDay, MaterialsWeek } from "../tables/WeeklyRealizationElements";

import type { MicroplanSummaryFilterProps } from "@/modules/pg/shared/weeklyRealization/weeklyRealizationCommon";
import type { Sheet } from "./ExportToXLSX";

type Filter = {
    week: number;
    year: number;
};

type SelectOptionItem = {
    label: string;
    short_label: string;
    value: Filter;
};

type Props = {
    className?: string;
    filter: MicroplanSummaryFilterProps;
    line_group_uuid: string;
    plant_uuid: string;
};

type State = {
    loading: boolean;
    show_modal: boolean;
    options: SelectOptionItem[];
    selected: SelectOptionItem[];
    loading_modal: boolean;
};

const subtractWeeks = (week: number, year: number, subtract_weeks: number): Filter[] => {
    const weeks: Filter[] = [];
    weeks.push({ week, year });

    while (subtract_weeks > 0) {
        const date = dateFromWeekAndShift(week - 1, year, 0);
        weeks.push({
            week: weekNumber(date),
            year: yearNumber(date),
        });
        week--;
        subtract_weeks--;
    }

    return weeks;
};

export default class ExportToXLSXButton extends React.Component<Props, State> {
    private mounted = false;

    state: State = {
        loading: true,
        show_modal: false,
        options: [],
        selected: [],
        loading_modal: false,
    };

    componentDidMount() {
        this.mounted = true;
        this.load();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    componentDidUpdate(prev_props: Props, prev_state: State) {
        if (prev_props.filter !== this.props.filter) {
            this.load();
        }

        if (prev_state.show_modal !== this.state.show_modal && this.state.show_modal === true) {
            this.setState({ selected: [] });
        }
    }

    mapWeeksToOptions = (weeks: Filter[]): SelectOptionItem[] => {
        return weeks.map(
            (w: Filter): SelectOptionItem => ({
                label: getDateRangeStrForWeek(w.week, w.year) + " " + translate("common.week", "Week") + " " + w.week,
                short_label: getDateRangeStrForWeek(w.week, w.year),
                value: w,
            })
        );
    };

    mapSelectedToSheets = async (selected: SelectOptionItem[]): Promise<Sheet[]> => {
        const sheets: Sheet[] = [];

        for (const option of selected) {
            const week = option.value.week;
            const year = option.value.year;
            const filter = {
                week: week,
                year: year,
                breakdown_level: this.props.filter.breakdown_level,
                produced_scrap_downtime: this.props.filter.produced_scrap_downtime,
            };

            // Do not cache simulation reports when preparing weekly realization report export
            const sim_report_add_to_cache = false;
            const merged_data = await loadWeeklyRealization(
                week,
                year,
                this.props.plant_uuid,
                this.props.line_group_uuid,
                sim_report_add_to_cache
            );
            if (merged_data) {
                let html_table: string | null = null;
                if (filter.breakdown_level === "day") {
                    html_table = renderToString(
                        <IntlProviderWrapper>
                            <MaterialsDay
                                merged_data={merged_data}
                                is_export={true}
                                filter={this.props.filter}
                                show_line_day_sum={false}
                                line_group_uuid={this.props.line_group_uuid}
                                produced_scrap_downtime={this.props.filter.produced_scrap_downtime}
                            />
                        </IntlProviderWrapper>
                    );
                } else if (filter.breakdown_level === "week") {
                    html_table = renderToString(
                        <IntlProviderWrapper>
                            <MaterialsWeek
                                merged_data={merged_data}
                                is_export={true}
                                filter={this.props.filter}
                                line_group_uuid={this.props.line_group_uuid}
                            />
                        </IntlProviderWrapper>
                    );
                } else {
                    html_table = renderToString(
                        <IntlProviderWrapper>
                            <WeeklyRealizationShift
                                is_export={true}
                                insights={[]}
                                onShowProductionModal={() => {}}
                                merged_data={merged_data}
                                filter={filter}
                                line_group_uuid={this.props.line_group_uuid}
                            />
                        </IntlProviderWrapper>
                    );
                }

                sheets.push({
                    html_table,
                    week_range: option.short_label,
                });
            }
        }

        return sheets;
    };

    load() {
        const weeks = subtractWeeks(this.props.filter.week, this.props.filter.year, 8);

        this.setState({
            options: this.mapWeeksToOptions(weeks),
        });
    }

    setLoading = (value: boolean) => {
        this.setState({ loading: value });
    };

    onClose = () => {
        this.setState({ show_modal: false });
    };

    onOpen = () => {
        this.setState({ show_modal: true });
    };

    onExport = async () => {
        this.setState({ loading_modal: true });

        const selected_sheets = await this.mapSelectedToSheets(this.state.selected);
        const { writeWeeklyRealizationXLSX } = await import("./ExportToXLSX");

        if (this.mounted) {
            this.setState({ loading_modal: false, show_modal: false });
        }

        writeWeeklyRealizationXLSX(selected_sheets);
    };

    onChange = async (selected: SelectOptionItem[]) => {
        this.setState({ selected: selected });
    };

    render() {
        const { className } = this.props;

        return (
            <>
                <button
                    disabled={this.state.loading || this.props.filter.produced_scrap_downtime !== "produced"}
                    className={classNames("btn btn-primary", className)}
                    style={{ float: "right" }}
                    onClick={this.onOpen}
                >
                    <span>{translate("Manufacturing.MicroplanSummary.export_to_xlsx", "Export To XLSX")}</span>
                </button>
                <Modal show={this.state.show_modal}>
                    <Modal.Body>
                        {this.state.loading_modal ? (
                            <div style={{ marginTop: "-40px" }}>
                                <Loader small={true} />
                            </div>
                        ) : (
                            <Select
                                value={this.state.selected}
                                options={this.state.options}
                                onChange={this.onChange}
                                isMulti
                            />
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-primary" onClick={this.onExport}>
                            {translate("Manufacturing.MicroplanSummary.export_to_xlsx", "Export To XLSX")}
                        </button>
                        <button className="btn btn-primary" onClick={this.onClose}>
                            {translate("common.close", "Close")}
                        </button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}
